import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tracks whether a horizontally scrolling element has hidden content on either side, and scrolls
 * it by most of its width. Updates on scroll and whenever the element or its first child resizes.
 * @returns {{ ref: React.RefObject<HTMLElement>, canScrollLeft: boolean, canScrollRight: boolean,
 *   scrollBy: (direction: -1 | 1) => void }}
 */
export const useScrollOverflow = () => {
    const ref = useRef(null);
    const [overflow, setOverflow] = useState({ left: false, right: false });

    const update = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        // 1px of slack: fractional widths can leave scrollLeft a hair short of max.
        const next = { left: el.scrollLeft > 1, right: el.scrollLeft < max - 1 };
        setOverflow((prev) => (prev.left === next.left && prev.right === next.right ? prev : next));
    }, []);

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        update();
        el.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => update());
        observer?.observe(el);
        if (el.firstElementChild) observer?.observe(el.firstElementChild);
        return () => {
            el.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
            observer?.disconnect();
        };
    }, [update]);

    const scrollBy = useCallback((direction) => {
        const el = ref.current;
        if (!el) return;
        el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
    }, []);

    return { ref, canScrollLeft: overflow.left, canScrollRight: overflow.right, scrollBy };
};
